import { useEffect, useRef, useState, type FormEvent } from 'react';
import { DotmSquare11 } from '~/components/ui/dotm-square-11';
import caseStyles from '~/deck2/case/case.module.css';
import { KeyHint } from '~/deck2/ui';
import type { CompanyLogo } from '~/routes/cases.logo';
import { Arrow } from './Arrow';
import { start } from './config';
import styles from './landing.module.css';

/** Where they work, as they typed it, with its logo, website and brand colour once those are found. */
export interface Company {
  name: string;
  logo: string | null;
  domain: string | null;
  colour: string | null;
}

/** How long the typing has to rest before the logo is looked up. */
const PAUSE_MS = 350;
/** How long "Next" waits for a lookup still on its way, so the chip can carry the logo. */
const NEXT_WAITS_MS = 1500;

const key = (typed: string) => typed.trim().toLowerCase();

const asked = new Map<string, Promise<CompanyLogo>>();
function lookUp(typed: string): Promise<CompanyLogo> {
  const k = key(typed);
  let answer = asked.get(k);
  if (!answer) {
    answer = fetch(`/cases/logo?q=${encodeURIComponent(typed.trim())}`)
      .then((res) => (res.ok ? (res.json() as Promise<CompanyLogo>) : { image: null, domain: null }))
      .catch(() => ({ image: null, domain: null }));
    asked.set(k, answer);
  }
  return answer;
}

/**
 * The logo's own colour: the most common strong colour in it, skipping white, black and greys, so a logo
 * on a white tile still gives its brand colour. Null for a logo with no colour of its own.
 */
async function colourOf(src: string): Promise<string | null> {
  const image = new Image();
  image.src = src;
  await image.decode();
  const size = 40;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return null;
  context.drawImage(image, 0, 0, size, size);
  const { data } = context.getImageData(0, 0, size, size);
  const hues = new Map<number, { weight: number; r: number; g: number; b: number }>();
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] < 200) continue;
    const [r, g, b] = [data[i], data[i + 1], data[i + 2]];
    const max = Math.max(r, g, b) / 255;
    const min = Math.min(r, g, b) / 255;
    const light = (max + min) / 2;
    const chroma = max - min;
    if (chroma < 0.2 || light < 0.12 || light > 0.92) continue;
    const hue = max === r / 255 ? ((g - b) / 255 / chroma + 6) % 6 : max === g / 255 ? (b - r) / 255 / chroma + 2 : (r - g) / 255 / chroma + 4;
    const bucket = Math.round(hue * 2) % 12;
    const at = hues.get(bucket) ?? { weight: 0, r: 0, g: 0, b: 0 };
    hues.set(bucket, { weight: at.weight + chroma, r: at.r + r * chroma, g: at.g + g * chroma, b: at.b + b * chroma });
  }
  const top = [...hues.values()].sort((a, b) => b.weight - a.weight)[0];
  if (!top) return null;
  const hex = (v: number) => Math.round(v / top.weight).toString(16).padStart(2, '0');
  return `#${hex(top.r)}${hex(top.g)}${hex(top.b)}`;
}

async function companyFor(typed: string): Promise<Company> {
  const { image, domain } = await lookUp(typed);
  const colour = image ? await colourOf(image).catch(() => null) : null;
  return { name: typed.trim(), logo: image, domain: image ? domain : null, colour };
}

/**
 * "Where do you work?": one field. When the typing rests, the company's logo is looked up through Deck's
 * rules and shows inside the field, and its colour goes to `onFound` so the page can take it on.
 */
export function WorkStep({
  active,
  initial,
  onFound,
  onDone,
}: {
  active: boolean;
  initial: Company | null;
  onFound: (company: Company | null) => void;
  onDone: (company: Company) => void;
}) {
  const [typed, setTyped] = useState(initial?.name ?? '');
  const [found, setFound] = useState<Company | null>(initial?.logo ? initial : null);
  // The name the lookup last answered for, logo or not. Until it answers for what is typed, the logo's place
  // shows Deck's dot matrix.
  const [answeredFor, setAnsweredFor] = useState(initial ? key(initial.name) : '');
  const field = useRef<HTMLInputElement>(null);
  // The latest of these, for the lookup that answers after a render or two.
  const report = useRef(onFound);
  report.current = onFound;
  const foundFor = useRef(found ? key(found.name) : '');
  const shown = found && key(found.name) === key(typed) ? found : null;
  const looking = active && key(typed).length >= 2 && answeredFor !== key(typed);

  useEffect(() => {
    if (active) field.current?.focus({ preventScroll: true });
  }, [active]);

  useEffect(() => {
    if (!active) return undefined;
    // A logo that no longer matches what is typed leaves straight away, and the page's colour with it.
    if (foundFor.current !== key(typed)) report.current(null);
    if (key(typed).length < 2) return undefined;
    let current = true;
    const timer = window.setTimeout(() => {
      void companyFor(typed).then((company) => {
        if (!current) return;
        const logo = company.logo ? company : null;
        foundFor.current = logo ? key(company.name) : '';
        setFound(logo);
        setAnsweredFor(key(company.name));
        report.current(logo);
      });
    }, PAUSE_MS);
    return () => {
      current = false;
      window.clearTimeout(timer);
    };
  }, [typed, active]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const name = typed.trim();
    if (!name) return;
    if (shown) return onDone(shown);
    const waited = await Promise.race([companyFor(name), new Promise<null>((resolve) => window.setTimeout(() => resolve(null), NEXT_WAITS_MS))]);
    onDone(waited?.logo ? waited : { name, logo: null, domain: null, colour: null });
  };

  return (
    <section className={caseStyles.question}>
      <header className={caseStyles.ask}>
        <h1 className={caseStyles.title}>{start.work.title}</h1>
      </header>
      <form className={styles.pillForm} onSubmit={submit}>
        <div className={styles.pill}>
          <div className={styles.pillTyped}>
            <input
              ref={field}
              className={styles.pillInput}
              type="text"
              name="company"
              autoComplete="organization"
              aria-label={start.work.label}
              placeholder={start.work.placeholder}
              maxLength={80}
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              disabled={!active}
            />
            {shown?.logo ? (
              <img key={shown.logo} className={styles.pillLogo} src={shown.logo} alt="" />
            ) : looking ? (
              <DotmSquare11 className={styles.pillLooking} size={26} dotSize={4} ariaLabel={start.work.looking} />
            ) : null}
          </div>
          <button type="submit" className={`${styles.button} ${styles.ink} ${styles.pillSubmit}`} disabled={!active || !typed.trim()}>
            {start.work.next}
            <Arrow />
          </button>
        </div>
      </form>
      <KeyHint parts={start.work.hint} />
    </section>
  );
}
